import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'hooks';

// routing
import Routes from './routes';

// project imports
import NavigationScroll from './layout/NavigationScroll';

import store from 'store';
import { setIsConnected, ready } from 'store/applicationReducer';
import { getUserInfo } from 'store/userReducer';
import { LOGOUT_ACTION } from 'store/rootReducer';

import webSocketManager, { ConnectionStatus } from 'socket/WebSocketManager';
import { useSessionTimer } from 'hooks/useSessionTimer';
import { sessionWarning, sessionExtended, fetchSessionInfo } from 'store/sessionReducer';

import ErrorBoundary from 'utils/ErrorBoundary';
import { setupInterceptors } from 'utils/setupInterceptors';
setupInterceptors();

import NotFound from './views/NotFound';

// ==============================|| APP ||============================== //

const App = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const application = useAppSelector((state) => state.application);
    const { isConnected, isReady } = application;
    const user = useAppSelector((state) => state.user);
    const { info: userInfo } = user;
    const requestLocation = useRef<string | null>(null);

    const sessionTimer = useRef<NodeJS.Timeout | null>(null);

    useSessionTimer();

    useEffect(() => {
        console.debug(`ready: ${isReady}, connected: ${isConnected}`);
        const location = window.location.pathname + window.location.search + window.location.hash;
        console.debug(`location : ${location}`);
        requestLocation.current = location;
        if (!userInfo) onCheckUser();

        webSocketManager
            .connect()
            ?.then(() => {
                dispatch(setIsConnected(true));
            })
            .catch((e) => {
                console.error(e);
                dispatch(setIsConnected(false));
            })
            .finally(() => {
                dispatch(ready());
            });

        const handleStatus = (status: ConnectionStatus) => {
            if (status === 'disconnected') dispatch(setIsConnected(false));
            if (status === 'connected') {
                dispatch(setIsConnected(true));
                // Re-sync on EVERY connect event, including short reconnects that never flip isConnected
                // (WebSocketManager only reports 'disconnected' after a 10s grace). Recovers a WARNING/
                // EXPIRED push missed while the socket was briefly down. Read userInfo from the store,
                // not the [] effect's captured closure.
                if (store.getState().user.info) dispatch(fetchSessionInfo());
            }
        };

        webSocketManager.addStatusListener(handleStatus);
        window.addEventListener('focus', onCheckUser);

        return () => {
            webSocketManager.disconnect();
            window.removeEventListener('focus', onCheckUser);
        };
    }, []);

    useEffect(() => {
        if (isConnected) {
            // Re-sync on (re)connect is handled in handleStatus (covers short reconnects too).
            // /session/check is a STOMP send: it does not touch the server's GD_LAST_ACTIVITY_TIME
            // sliding window (that filter excludes /ws), so it never extends the session.
            webSocketManager.send('/session/check', { hi: 'hi' });

            if (!sessionTimer.current) {
                // 서버가 세션 상태(경고/연장/만료)를 push하는 채널.
                // 백엔드는 convertAndSendToUser(user, "/topic/session", ...) 로 보내므로 여기서 /user/topic/session 을 구독한다.
                webSocketManager.subscribe('/user/topic/session', (message) => {
                    try {
                        const payload = JSON.parse(message.body) as {
                            type: 'WARNING' | 'EXTENDED' | 'EXPIRED';
                            remainingSeconds?: number;
                        };
                        switch (payload.type) {
                            case 'WARNING':
                                // ignore a malformed payload rather than snapping the countdown to 0
                                if (typeof payload.remainingSeconds === 'number') {
                                    dispatch(sessionWarning(payload.remainingSeconds));
                                }
                                break;
                            case 'EXTENDED':
                                if (typeof payload.remainingSeconds === 'number') {
                                    dispatch(sessionExtended(payload.remainingSeconds));
                                }
                                break;
                            case 'EXPIRED':
                                dispatch({ type: LOGOUT_ACTION });
                                break;
                            default:
                                break;
                        }
                    } catch (e) {
                        console.error('[WS] Failed to parse /user/topic/session message', e);
                    }
                });

                webSocketManager.subscribe('/user/topic/errors', (message) => {
                    console.warn('[RECV] /user/topic/errors:', message.body);
                    // read userInfo from the store (not the effect-captured closure, which is stale)
                    if (message.body === 'Unauthorized' && store.getState().user.info) {
                        dispatch({ type: LOGOUT_ACTION });
                    }
                });

                sessionTimer.current = setInterval(() => {
                    webSocketManager.send('/session/check', { hi: 'hi' });
                }, 1000 * 30);
            }
        } else {
            if (sessionTimer.current) {
                clearInterval(sessionTimer.current);
                sessionTimer.current = null;
            }

            webSocketManager.unsubscribe('/user/topic/session');
            webSocketManager.unsubscribe('/user/topic/errors');
        }

        return () => {
            if (sessionTimer.current) {
                clearInterval(sessionTimer.current);
                sessionTimer.current = null;
            }

            webSocketManager.unsubscribe('/user/topic/session');
            webSocketManager.unsubscribe('/user/topic/errors');
        };
    }, [isConnected]);

    useEffect(() => {
        if (userInfo) {
            if (requestLocation.current) {
                navigate(requestLocation.current);
                requestLocation.current = null;
            }
        } else {
            navigate('/login');
        }
    }, [userInfo]);

    const onCheckUser = () => {
        dispatch(getUserInfo());
    };

    const render = () => {
        console.debug('Render App.');
        if (!isReady) return;
        if (!isConnected) return <NotFound />;

        return (
            <ErrorBoundary>
                <NavigationScroll>
                    <Routes />
                </NavigationScroll>
            </ErrorBoundary>
        );
    };

    return render();
};

export default App;
